import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../services/errorMessages';
import { FiPlus, FiTrash2, FiEdit2, FiX } from 'react-icons/fi';
import ConfirmModal from '../components/ConfirmModal';
import Dropdown from '../components/Dropdown';
import './Pages.css';

const Activities = () => {
  const { isAdmin } = useAuth();
  const [activities, setActivities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchActivities = async () => {
    try {
      const [actRes, tmplRes] = await Promise.all([
        api.get('/activities'),
        api.get('/templates'),
      ]);
      setActivities(actRes.data);
      setTemplates(tmplRes.data);
    } catch {
      // fetch failed silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchActivities(); }, []);

  const openEdit = (a) => {
    setEditing(a);
    setName(a.name);
    setTemplateId(a.template_id || '');
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const tmpl = templates.find(t => t.id === Number(templateId));
      const autoType = tmpl?.type === 'weekly' ? 'weekly' : 'standard';
      if (editing) {
        await api.put(`/activities/${editing.id}`, { name, activity_type: autoType, template_id: templateId || null });
      } else {
        await api.post('/activities', { name, activity_type: autoType, template_id: templateId || null });
      }
      setShowModal(false);
      setEditing(null);
      setName('');
      setTemplateId('');
      fetchActivities();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDelete = (id) => {
    setConfirmAction({
      title: 'აქტივობის წაშლა', message: 'ნამდვილად გსურთ წაშლა?', type: 'delete',
      onConfirm: async () => { try { await api.delete(`/activities/${id}`); fetchActivities(); } catch { /* ignored */ } setConfirmAction(null); },
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>აქტივობები</h1>
          <p className="page-subtitle">აქტივობების მართვა</p>
        </div>
        <div className="header-actions">
          <Dropdown
            value={filterTemplate}
            onChange={(val) => setFilterTemplate(val)}
            options={[
              { value: '', label: 'ყველა' },
              ...templates.map((t) => ({ value: String(t.id), label: t.title })),
            ]}
            placeholder="დანართი"
          />
          {isAdmin && (
            <button className="btn-primary" onClick={() => { setEditing(null); setName(''); setTemplateId(''); setError(''); setShowModal(true); }}>
              <FiPlus /> დამატება
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">იტვირთება...</div>
      ) : (
        <div className="activities-grid">
          {activities.filter((a) => !filterTemplate || String(a.template_id) === filterTemplate).map((a) => (
            <div key={a.id} className="activity-card">
              <div className="activity-top">
                <span className="activity-name">{a.name}</span>
              </div>
              <div className="activity-bottom">
                <div className="activity-badges">
                  {a.template_id ? (() => {
                    const tmpl = templates.find(t => t.id === a.template_id);
                    return tmpl ? <span className="badge badge-green" style={{fontSize: 11}}>{tmpl.title}</span> : null;
                  })() : <span className="badge badge-blue" style={{fontSize: 11}}>ყველა დანართი</span>}
                </div>
                {isAdmin && (
                  <div className="activity-actions">
                    <button className="btn-icon btn-edit" onClick={() => openEdit(a)} title="რედაქტირება">
                      <FiEdit2 />
                    </button>
                    <button className="btn-icon btn-danger" onClick={() => handleDelete(a.id)} title="წაშლა">
                      <FiTrash2 />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {activities.filter((a) => !filterTemplate || String(a.template_id) === filterTemplate).length === 0 && <div className="empty-state-card">აქტივობები არ არის</div>}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditing(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'აქტივობის რედაქტირება' : 'ახალი აქტივობა'}</h3>
              <button className="btn-icon" onClick={() => { setShowModal(false); setEditing(null); }}><FiX /></button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label>დასახელება</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="შეიყვანეთ აქტივობის დასახელება" required />
              </div>
              <Dropdown
                label="დანართი"
                value={templateId}
                onChange={(val) => setTemplateId(val)}
                options={[
                  { value: '', label: 'ყველა დანართი' },
                  ...templates.map((t) => ({ value: t.id, label: t.title })),
                ]}
                placeholder="აირჩიეთ დანართი"
              />
              <button type="submit" className="btn-primary btn-full" disabled={!name.trim()}>{editing ? 'შენახვა' : 'დამატება'}</button>
            </form>
          </div>
        </div>
      )}
      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Activities;
